import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Field } from "@/components/app/ui";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConsentPanel, TechPanel } from "@/components/app/OrgPanels";

const FACT_LABELS = {
  in_scope: "Within DPDP territorial scope (S.3)", relies_on_consent: "Relies on consent", has_existing_data: "Holds pre-commencement data",
  consumer_facing: "Consumer-facing", uses_consent_manager: "Uses Consent Manager", processes_children_data: "Processes children's data",
  sdf: "Significant Data Fiduciary (designated / likely)", uses_processors: "Uses Data Processors", cross_border: "Cross-border processing",
  third_schedule_entity: "Third Schedule class entity", sensitive_data: "Sensitive categories", large_scale: "Large-scale Data Principals", has_privacy_lead: "Privacy lead in place",
};

export const FactsPanel = ({ facts }) => (
  <Panel title="Applicability facts (derived from profile)" testId="facts-panel">
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(FACT_LABELS).map(([k, l]) => (
        <div key={k} data-testid={`fact-${k}`} className="flex items-center gap-2 text-sm">
          {facts?.[k] ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-slate-300" />}
          <span className={facts?.[k] ? "text-slate-800" : "text-slate-400"}>{l}</span>
          <code className="ml-auto text-[10px] text-slate-400">{k}</code>
        </div>
      ))}
    </div>
  </Panel>
);

export default function Organisation() {
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const load = () => api.get("/org").then((r) => setO(r.data));
  useEffect(() => { load(); }, []);
  if (!o) return null;
  const p = o.profile || {};
  const isAdmin = user.role === "admin";
  return (
    <div className="space-y-6">
      <PageHeader title="Organisation" subtitle="Organisation profile drives the applicability engine. Editing the profile re-evaluates the control set."
        actions={user.role === "admin" && <Link to="/onboarding"><Button variant="outline" data-testid="edit-profile-button"><Pencil className="mr-1.5 h-4 w-4" />Edit profile & re-run applicability</Button></Link>} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Profile" testId="org-profile">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company">{o.name}</Field><Field label="Industry">{p.industry}</Field>
            <Field label="Size">{p.company_size}</Field><Field label="Employees">{p.employees}</Field>
            <Field label="Customers / users">{p.customers_band}</Field><Field label="Revenue band">{p.revenue_band}</Field>
            <Field label="Countries">{(p.countries || []).join(", ")}</Field><Field label="SDF status">{p.sdf_status}</Field>
            <Field label="Data categories">{(p.data_categories || []).join(", ")}</Field><Field label="Technologies">{[...(p.technologies || []), ...(p.other_technologies || [])].join(", ")}</Field>
            <Field label="Business model">{p.business_model}</Field>
            <Field label="Collection channels">{(p.collection_channels || []).join(", ")}</Field>
            <Field label="Collection sources">{(p.collection_sources || []).join(", ")}</Field>
            {p.collection_description && <Field label="Data collection description">{p.collection_description}</Field>}
          </div>
        </Panel>
        <FactsPanel facts={o.facts} />
        <Panel title="Security & Governance" testId="org-governance">
          <p className="text-sm text-slate-600">The governance baseline from onboarding now lives on the Security & Governance page, with status, description, evidence, owner and review date per control.</p>
          <Link to="/governance"><Button size="sm" variant="outline" className="mt-3" data-testid="org-goto-governance">Open Security & Governance</Button></Link>
        </Panel>
        <ConsentPanel isAdmin={isAdmin} />
        <TechPanel key={JSON.stringify([p.technologies, p.other_technologies])} profile={p} isAdmin={isAdmin} onSaved={load} />
        <Panel title="Subscription & entitlements" testId="org-plan">
          <div className="text-sm"><b>{o.plan_details.label}</b> plan</div>
          <div className="mt-2 flex flex-wrap gap-1.5">{o.plan_details.features.map((f) => <span key={f} className="rounded border bg-slate-50 px-2 py-0.5 text-xs text-slate-600">{f.replace(/_/g, " ")}</span>)}</div>
          <p className="mt-3 text-xs text-slate-400">Billing and plan changes: Coming Soon.</p>
        </Panel>
      </div>
    </div>
  );
}
